import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { CatCard } from "blocks-nextjs/blocks-react/components/Card/Card";
import { CatCarousel } from "blocks-nextjs/blocks-react/components/Carousel/Carousel";
import { CatCarouselItem } from "blocks-nextjs/blocks-react/components/CarouselItem/CarouselItem";
import { CatHeading } from "blocks-nextjs/blocks-react/components/Heading/Heading";
import { CatIconTrendingDown } from "blocks-nextjs/blocks-react/components/Icons/TrendingDown/TrendingDown";
import { CatIconTrendingUp } from "blocks-nextjs/blocks-react/components/Icons/TrendingUp/TrendingUp";
import { CatLoadingIndicator } from "blocks-nextjs/blocks-react/components/LoadingIndicator/LoadingIndicator";
import React from "react";
import { useEnergyMetricsCards } from "../../hooks/useEnergyMetricsCards";
import { MetricsEnergyEnum } from "../../pages/Projects/MetricsData";
import APIError from "../APIError/APIError";
const API_STATUS_ERROR = "error";
const API_STATUS_PENDING = "pending";
const UNIT_KWH = "kWh";
const UNIT_TONNES = "tonnes";
const LIVE_METRIC_LABELS = {
    [MetricsEnergyEnum.residualEnergy]: "Residual Energy balance ESS"
};
const formatValueToTwoDecimalPoints = (value) => value.toLocaleString(undefined, { maximumFractionDigits: 2 });
const formatTrendPercentage = (value) => `${value.toFixed(1)}%`;
// Live tab only surfaces the current-day snapshot cards; trend/historic breakdowns stay on the Historic Data tab.
const LIVE_METRIC_NAMES = [
    MetricsEnergyEnum.essEnergy,
    MetricsEnergyEnum.machineEnergy,
    MetricsEnergyEnum.residualEnergy
];
// The Live cards are a fixed set, so an absent metric renders as an all-dash card rather than disappearing.
const createPlaceholderMetric = (name) => ({
    currentDay: null,
    metric: {
        lastDay: { energyTrend: null, previousValue: null, value: null },
        lastMonth: { energyTrend: null, value: null },
        lastWeek: { energyTrend: null, value: null }
    },
    name
});
const combineProductivityMetric = (metricsData) => {
    const hasFigures = (item) => [
        item?.currentDay,
        item?.metric?.lastDay?.energyTrend,
        item?.metric?.lastDay?.previousValue,
        item?.metric?.lastDay?.value
    ].some((figure) => typeof figure === "number");
    // ProjectDetails always supplies both sides, as all-null placeholders when the API omits them.
    const reported = (item) => (hasFigures(item) ? item : undefined);
    const hauler = reported(metricsData.find((item) => item.name === MetricsEnergyEnum.haulerDailyPrediction));
    const loader = reported(metricsData.find((item) => item.name === MetricsEnergyEnum.loadOutDailyProduction));
    if (!hauler && !loader)
        return createPlaceholderMetric(MetricsEnergyEnum.productivity);
    const isNullish = (value) => value === null || value === undefined;
    const sumOrNull = (first, second) => isNullish(first) && isNullish(second) ? null : (first ?? 0) + (second ?? 0);
    const haulerLastDay = hauler?.metric?.lastDay;
    const loaderLastDay = loader?.metric?.lastDay;
    // A reported side missing either total would skew the ratio, so drop the trend instead.
    const hasBothTotals = (item) => !item || (!isNullish(item.metric?.lastDay?.value) && !isNullish(item.metric?.lastDay?.previousValue));
    const previousTotal = sumOrNull(haulerLastDay?.previousValue, loaderLastDay?.previousValue);
    const currentTotal = sumOrNull(haulerLastDay?.value, loaderLastDay?.value);
    // The two trends share no common base, so derive the combined one from the summed totals.
    const combinedTrend = previousTotal && hasBothTotals(hauler) && hasBothTotals(loader)
        ? (((currentTotal ?? 0) - previousTotal) / previousTotal) * 100
        : null;
    return {
        currentDay: sumOrNull(hauler?.currentDay, loader?.currentDay),
        metric: {
            lastDay: {
                energyTrend: combinedTrend,
                previousValue: previousTotal,
                value: currentTotal
            },
            lastMonth: { energyTrend: null, value: null },
            lastWeek: { energyTrend: null, value: null }
        },
        name: MetricsEnergyEnum.productivity
    };
};
const buildLiveMetrics = (metricsData) => [
    ...LIVE_METRIC_NAMES.map((name) => metricsData.find((item) => item.name === name) ?? createPlaceholderMetric(name)),
    combineProductivityMetric(metricsData)
];
const LiveMetrics = ({ projectId, refreshTimestamp, startDate }) => {
    const { energyMetricsApiStatus, energyMetricsData, refetchProjectEnergyMetrics } = useEnergyMetricsCards(projectId, startDate, refreshTimestamp);
    const visibleMetrics = buildLiveMetrics(energyMetricsData ?? []);
    if (energyMetricsApiStatus === API_STATUS_ERROR) {
        return (_jsx("div", { "data-test": "live-metrics-error", role: "alert", children: _jsx(APIError, { refreshHandler: refetchProjectEnergyMetrics }) }));
    }
    if (energyMetricsApiStatus === API_STATUS_PENDING) {
        return (_jsx("div", { "aria-label": "Loading live metrics", "aria-live": "polite", className: "flex justify-center items-center min-h-[12vh] w-full", "data-test": "live-metrics-loading-indicator", role: "status", children: _jsx(CatLoadingIndicator, {}) }));
    }
    return (
    // Carousel (not a static grid) so cards become a draggable strip instead of
    // overflowing once the page is too narrow for all 4 side-by-side (e.g. half-screen).
    // navigation="overlaid" matches the legacy Metrics carousel's side arrows; CSS in
    // index.css hides them once all 4 cards fit on screen.
    _jsx(CatCarousel, { arialabel: "Live metrics", behavior: "overflow", className: "live-metrics-carousel w-full", "data-test": "live-metrics-grid", hideSlideCount: true, loop: false, mouseDragging: true, navigation: "overlaid", children: visibleMetrics.map((item) => {
            const dataTestElementName = item.name.replace(/ +/g, "");
            const unit = item.name === MetricsEnergyEnum.productivity ? UNIT_TONNES : UNIT_KWH;
            const label = LIVE_METRIC_LABELS[item.name] ?? item.name;
            const trend = item.metric?.lastDay?.energyTrend ?? null;
            return (_jsx(CatCarouselItem, { arialabel: label, className: "h-full w-full", children: _jsxs(CatCard, { className: "[--cat-card-border-color:transparent] [&::part(base)]:shadow-[0_1px_4px_rgba(0,0,0,0.2)]", "data-test": `live-metric-card-${dataTestElementName}`, children: [_jsx(CatHeading, { className: "[--cat-theme-color-content-default:#565656] [--cat-theme-typography-label-font-family:'Roboto_Flex']", "data-test": `${dataTestElementName}-label`, variant: "label", children: label }), _jsxs("div", { className: "flex items-center gap-1 mt-0.5", children: [_jsx("span", { className: "flex items-baseline gap-1", "data-test": `${dataTestElementName}-currentDay`, children: typeof item.currentDay === "number" ? (_jsxs(_Fragment, { children: [_jsx("span", { className: "text-2xl font-semibold leading-8 font-['Roboto_Flex']", children: formatValueToTwoDecimalPoints(item.currentDay) }), _jsx("span", { className: "text-sm font-normal leading-5 font-['Roboto_Flex']", children: unit })] })) : (_jsx("span", { className: "text-2xl font-semibold leading-8 text-gray-700 font-['Roboto_Flex']", children: "\u2014" })) }), _jsx("span", { className: "flex min-w-0 items-center gap-1 ps-2 font-['Roboto_Flex'] text-xs leading-4 text-[#565656]", children: trend === null ? (_jsx("span", { className: "min-w-0 truncate", children: "\u2014 vs prev day" })) : (_jsxs(_Fragment, { children: [trend !== 0 &&
                                                (trend > 0 ? (_jsx(CatIconTrendingUp, { "data-test": `${dataTestElementName}-trending-up` })) : (_jsx(CatIconTrendingDown, { "data-test": `${dataTestElementName}-trending-down` }))), _jsxs("span", { className: "min-w-0 truncate", "data-test": `${dataTestElementName}-lastDay-energyTrend`, children: [formatTrendPercentage(trend), " vs prev day"] })] })) })] }), _jsx("div", { className: "text-xs leading-4 text-[#565656]", "data-test": `${dataTestElementName}-today-so-far`, children: "Today so far" })] }) }, item.name));
        }) }));
};
export default LiveMetrics;
